import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useActionCreator from "@redux/hooks/useActionCreator";

import { Table } from "@pentabd/ui";

import {
  DeleteConfirmationModal,
  ErrorModal,
  SelectControllerVertical,
  SuccessModal,
} from "@components";
import { AnnualProcurementTableColumn } from "./AnnualProcurementPlanColumn";

import { useAnnualProcurementPlans } from "@hooks/procurements/annualProcurementPlan/useAnnualProcurementPlans";
import { AnnualProcurementsResponseType } from "@type/procurements/annualProcurementPlan";
import { Pagination } from "@type/procurements/pagination";
import { getAccess } from "@helpers/permissionController";

import { TablePagination } from "@helpers/tablePagination";

import { useUser } from "@hooks/useUser";
import { getConvertedBudgetYearList } from "@helpers/baseDataConvert";
import { useBudgetYearList } from "@hooks/inventory/budget/useBudgetYearList";

export default function AnnualProcurementPlanTable() {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [itemId, setItemId] = useState<number>();
  const [itemName, setItemName] = useState<string>("");

  const { t } = useTranslation();
  const { data: budgetYear } = useBudgetYearList();
  const { control, setValue, register } = useForm();
  const navigate = useNavigate();

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { annualProcurementPlansState, handleQuery } =
    useAnnualProcurementPlans();
  const { appDeleteActionHandler } = useActionCreator();

  const { getData, deleteSuccess, deleteError } = annualProcurementPlansState;
  const { data, pagination } =
    getData || ({} as AnnualProcurementsResponseType);
  const { page, total } = pagination || ({} as Pagination);

  const createSuccessAction = () => {
    setIsSuccessModalOpen(false);
    handleQuery("page", page);
  };
  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    handleQuery("page", page);
  };

  const closeConfirmationModel = () => setIsConfirmationModalOpen(false);

  const openConfirmationModel = (itemName: string, id: string | number) => {
    setItemId(id as number);
    setItemName(itemName);
    setIsConfirmationModalOpen(true);
  };

  const deleteAction = () => {
    closeConfirmationModel();
    appDeleteActionHandler(itemId as number);
  };

  useEffect(() => {
    switch (true) {
      case deleteSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case !!deleteError: {
        setIsErrorModalOpen(true);
        break;
      }
      default: {
        setIsSuccessModalOpen(false);
        setIsErrorModalOpen(false);
        break;
      }
    }
  }, [deleteError, deleteSuccess]);

  useEffect(() => {
    const reset = () => {
      setIsSuccessModalOpen(false);
      setIsErrorModalOpen(false);
      setIsConfirmationModalOpen(false);
    };

    return reset();
  }, []);

  return (
    <div className="pt-10 border-top">
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={!!deleteError && deleteError?.response?.data?.message}
        />
      )}
      {/* modal end */}

      {/* delete confirmation modal */}
      {isConfirmationModalOpen && (
        <DeleteConfirmationModal
          subtitle={itemName}
          isModalOpen={isConfirmationModalOpen}
          onClose={closeConfirmationModel}
          action={deleteAction}
        />
      )}

      <Table
        header={{
          title: t("PROCUREMENT_ANNUAL_PLAN.TABLE.HEADING"),
          badge: {
            label: `${total || 0}`,
            size: "sm",
            type: "primary",
          },
          buttons: [
            <div className="row pb-0 mb-0">
              <SelectControllerVertical
                label="PROCUREMENT_ANNUAL_PLAN.TABLE.FILTER.YEAR"
                name="office"
                register={{ ...register("year") }}
                options={getConvertedBudgetYearList(budgetYear)}
                setValue={setValue}
                selectAction={(e) => handleQuery("year", e)}
                control={control}
                portal
                withoutLabel
              />
            </div>,
          ],
        }}
        columns={AnnualProcurementTableColumn(
          t,
          getAccess,
          navigate,
          roles,
          openConfirmationModel,
        )}
        // actions={[
        //   {
        //     customIcon: (
        //       <Button type="light" size="sm" fill="outline">
        //         {t("PROCUREMENT_ANNUAL_PLAN.TABLE.ACTIONS.DETAILS_BUTTON_TEXT")}
        //       </Button>
        //     ),
        //     onClick: (id) => {
        //       navigate(`${ROUTES.DETAILS}/${id}`);
        //     },
        //   },
        // ]}
        // edit={
        //   getAccess(roles, "PROCUREMENT_HR")
        //     ? {
        //         customIcon: <IconEdit01 size={20} fill="subtitle1" />,
        //         onClick: (id) => {
        //           navigate(`${id}`);
        //         },
        //       }
        //     : {
        //         onClick: (id) => {},
        //       }
        // }
        // remove={
        //   getAccess(roles, "PROCUREMENT_HR")
        //     ? {
        //         customIcon: <IconTrash01 size={20} fill="danger" />,
        //         onClick: (id) => {
        //           appDeleteActionHandler(id);
        //         },
        //       }
        //     : {
        //         onClick: (id) => {},
        //       }
        // }
        rows={data || []}
        pagination={TablePagination(pagination, handleQuery)}
      />
    </div>
  );
}
